/**
 * Email + password sign-in (operator decision 17 Oct 2026), the second method beside Google:
 *   POST /auth/password/sign-in, POST /auth/password/change, POST /users/{id}/password-reset, DELETE /users/{id}/password.
 * The messages a person sees, and the checks a new password must pass before it is sent, live here so the sign-in
 * card, the must-change screen and Settings → People say the same thing.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { INVALID_CREDENTIALS_MESSAGE, PASSWORD_LOCKOUT_MINUTES, PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH } from '@amplispot/contracts';
import { ApiFailure, call } from '../api';
import { keys } from './data';

/** What went wrong with a password sign-in, in words the person can act on. Never says whether the email exists. */
export function passwordSignInError(e: unknown): string {
  if (e instanceof ApiFailure) {
    if (e.code === 'invalid_credentials') return INVALID_CREDENTIALS_MESSAGE;
    // 429 covers both the per-IP / per-email rate limit and the lockout; the server's text says which and for how long.
    if (e.status === 429) return e.body?.error.message ?? lockoutFallback();
    if (e.code === 'validation_failed') return 'Enter a full email address, for example name@amplispot.com, and your password.';
    if (e.status === 0) return e.message;
  }
  return e instanceof Error ? e.message : 'Sign-in failed. Try again.';
}

export const lockoutFallback = (): string =>
  `Too many tries. Wait ${PASSWORD_LOCKOUT_MINUTES} minutes and try again, or ask a manager to reset your password.`;

export interface NewPasswordErrors {
  new_password?: string;
  confirm?: string;
}

/** The same rules the server applies (10–256 characters, not the current one), plus the confirm box matching. */
export function checkNewPassword(current: string, next: string, confirm: string): NewPasswordErrors {
  const out: NewPasswordErrors = {};
  if (next.length < PASSWORD_MIN_LENGTH) out.new_password = `Use at least ${PASSWORD_MIN_LENGTH} characters.`;
  else if (next.length > PASSWORD_MAX_LENGTH) out.new_password = `Use at most ${PASSWORD_MAX_LENGTH} characters.`;
  else if (current && next === current) out.new_password = 'Choose a password different from the current one.';
  if (!out.new_password && confirm !== next) out.confirm = 'The two new passwords do not match.';
  return out;
}

/** What went wrong with POST /auth/password/change. Field-level where it belongs to one box. */
export function passwordChangeError(e: unknown): { field?: 'current_password' | 'new_password'; message: string } {
  if (e instanceof ApiFailure) {
    if (e.code === 'invalid_credentials') return { field: 'current_password', message: 'That is not your current password. Check it and try again.' };
    if (e.status === 429) return { message: e.body?.error.message ?? lockoutFallback() };
    if (e.code === 'conflict') return { message: 'You sign in with Google and have no password to change. Ask a manager if you need one.' };
    const fe = e.fieldErrors;
    if (fe.new_password) return { field: 'new_password', message: fe.new_password };
    if (fe.current_password) return { field: 'current_password', message: fe.current_password };
  }
  return { message: e instanceof Error ? e.message : 'The password was not changed. Try again.' };
}

/** POST /users/{id}/password-reset: a new temporary password, returned once. */
export function usePasswordReset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => call('users.passwordReset', { params: { id } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.users }),
  });
}

/** DELETE /users/{id}/password: Google sign-in only from then on. */
export function usePasswordRemove() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => call('users.passwordRemove', { params: { id } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.users }),
  });
}
